import { useEffect, useRef, type ReactNode } from "react";
import classNames from "classnames";

export const getSuggestionOptionId = (menuId: string, index: number) =>
  `${menuId}-option-${index}`;

interface Props<Item> {
  activeIndex: number;
  className?: string;
  emptyState?: ReactNode;
  getItemKey: (item: Item) => string;
  getItemLabel: (item: Item) => ReactNode;
  getItemTestId: (item: Item) => string;
  id: string;
  items: Item[];
  testId: string;
  onActiveIndexChange: (index: number) => void;
  onSelect: (item: Item) => void;
}

const SuggestionMenu = <Item,>({
  activeIndex,
  className,
  emptyState,
  getItemKey,
  getItemLabel,
  getItemTestId,
  id,
  items,
  testId,
  onActiveIndexChange,
  onSelect,
}: Props<Item>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const activeOptionId = getSuggestionOptionId(id, activeIndex);
    const element = containerRef.current?.querySelector(
      `#${CSS.escape(activeOptionId)}`
    );
    element?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, id]);

  if (items.length === 0 && emptyState == null) return null;

  return (
    <div
      className={classNames(
        className,
        "scrollbar-slim overflow-auto rounded border border-gray-100 bg-white text-sm shadow-sm",
        items.length === 0 ? "px-3 py-2 text-gray-500" : "py-1"
      )}
      data-testid={testId}
      id={id}
      ref={containerRef}
      role="listbox"
    >
      {items.length === 0
        ? emptyState
        : items.map((item, index) => (
            <button
              aria-selected={index === activeIndex}
              className={classNames(
                "block w-full px-3 py-2 text-left",
                index === activeIndex
                  ? "bg-gray-100 text-gray-950"
                  : "hover:bg-gray-50"
              )}
              data-testid={getItemTestId(item)}
              id={getSuggestionOptionId(id, index)}
              key={getItemKey(item)}
              role="option"
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => onActiveIndexChange(index)}
              onClick={() => onSelect(item)}
            >
              {getItemLabel(item)}
            </button>
          ))}
    </div>
  );
};

export default SuggestionMenu;
